// src/hooks/use-auth.tsx
import { createContext, useContext, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import type { User } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";

interface AuthContextType {
  user: User | null;
  isAuthLoading: boolean;
  login: (username: string, referralCode?: string, adminPassword?: string) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const { data: sessionUser, isLoading: isSessionLoading } = useQuery<User | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", "/api/auth/me");
        if (res.status === 401) return null;
        return (await res.json()) as User;
      } catch {
        return null;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const loginMutation = useMutation({
    mutationFn: async ({ 
      username,
      referralCode,
      adminPassword,
     }: { 
      username: string;
      referralCode?: string;
      adminPassword?: string;
     }): Promise<User> => {
      const res = await apiRequest("POST", "/api/auth/login", { username, referralCode, adminPassword });
      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw new Error(errorData?.message || "Login failed. Please check the username and try again.");
      }
      return (await res.json()) as User;
    },
    onSuccess: (user) => {
      // On successful login, the server creates a session cookie.
      // We update the "me" query data to reflect the new authenticated user.
      queryClient.setQueryData(['me'], user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await apiRequest("POST", "/api/auth/logout");
      if (!res.ok) {
        const err = await res.text().catch(() => "");
        throw new Error(err || "Logout failed");
      }
      return;
    },
    onSuccess: async () => {
      queryClient.clear();
      setLocation("/login");
    },
  });

  const login = async (username: string, referralCode?: string, adminPassword?: string): Promise<User> => {
    return loginMutation.mutateAsync({ username, referralCode, adminPassword });
  };

  const logout = async (): Promise<void> => {
    await logoutMutation.mutateAsync();
  };

  // Combine session loading and mutation pending states for a complete loading picture.
  const isAuthLoading = isSessionLoading || loginMutation.isPending || logoutMutation.isPending;

  const value: AuthContextType = {
    user: sessionUser || null,
    isAuthLoading,
    login,
    logout,
  };

  if (isSessionLoading) { // Only show initial screen-blocking loader for the session check
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <span className="text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
